/**
 * Capability-gated hardware video codec preference.
 *
 * Reorders codec *families* so MediaCapabilities-reported hardware paths
 * rank first: HEVC HW → AV1 HW → H.264 HW → software last resort.
 *
 * DO NOT reorder profiles within a family. Chromium lists H.264 profiles
 * hardware-encoder-first in its natural capability order; forcing Main
 * (`4d00*`) ahead of Constrained Baseline (`42e0*`) pushes Windows hosts
 * onto the OpenH264 software encoder instead of Media Foundation ExternalEncoder.
 *
 * Awaitable: callers (applyPreConnectionOptimizations) MUST await this before
 * creating an offer so setCodecPreferences reflects the HW-gated order.
 *
 * Probe-fail behavior: missing MediaCapabilities / encodingInfo / decodingInfo,
 * probe throw, or unsupported results → returns the original codec order
 * unchanged (never throws).
 */

export type HardwareCodecPreferenceMode = 'encode' | 'decode';

/** Locked HW family preference when MediaCapabilities reports powerEfficient. */
// export const HW_VIDEO_CODEC_FAMILY_ORDER = ['h265', 'av1', 'h264'] as const;
export const HW_VIDEO_CODEC_FAMILY_ORDER = ['h264'] as const;

type RtpCodecCapabilityLike = {
  mimeType: string;
};

/** Normalize mimeType to a codec family key (h264, h265, vp8, …). */
export function videoCodecFamily(mimeType: string): string {
  const mime = mimeType.toLowerCase();
  if (mime.includes('h264') || mime.includes('avc')) return 'h264';
  if (mime.includes('h265') || mime.includes('hevc')) return 'h265';
  if (mime.includes('vp8')) return 'vp8';
  if (mime.includes('vp9')) return 'vp9';
  if (mime.includes('av1')) return 'av1';
  return mime.split(';')[0]?.trim() || mime;
}

/** True when preferred token matches this codec's family (h265 ↔ HEVC). */
export function codecMatchesPreferred(mimeType: string, preferred: string): boolean {
  const family = videoCodecFamily(mimeType);
  const pref = preferred.toLowerCase();
  if (pref === 'hevc' || pref === 'h265') return family === 'h265';
  if (pref === 'avc' || pref === 'h264') return family === 'h264';
  return family === pref || mimeType.toLowerCase().includes(pref);
}

function toContentType(mimeType: string): string {
  return mimeType.includes('/') ? mimeType : `video/${mimeType}`;
}

type MediaCapabilitiesProbeResult = {
  supported: boolean;
  smooth?: boolean;
  powerEfficient?: boolean;
};

/**
 * The encode/decode the probe should ask about. Answering "can you do 1280×720 at
 * 3 Mbps" when the session actually runs 896×480 can demote a codec a weak mobile
 * decoder would in fact handle in hardware, so callers pass their real target.
 */
export type HardwareProbeVideoTarget = {
  width: number;
  height: number;
  /** bps, matching WebRtcOptimizationConfig.maxBitrate. */
  bitrate: number;
  framerate: number;
};

/** Used only when a caller supplies no target (desktop ShadowCam03 shape). */
export const DEFAULT_HW_PROBE_VIDEO: HardwareProbeVideoTarget = {
  width: 1280,
  height: 720,
  bitrate: 3_000_000,
  framerate: 60,
};

type MediaCapabilitiesProbe = (configuration: {
  type: string;
  video?: {
    contentType: string;
    width: number;
    height: number;
    bitrate: number;
    framerate: number;
  };
  hardwareAcceleration?: string;
}) => Promise<MediaCapabilitiesProbeResult>;

function resolveProbe(
  mode: HardwareCodecPreferenceMode,
): MediaCapabilitiesProbe | null {
  const mediaCapabilities = globalThis.navigator?.mediaCapabilities;
  if (!mediaCapabilities) return null;

  if (mode === 'encode') {
    const encodingInfo = (
      mediaCapabilities as MediaCapabilities & {
        encodingInfo?: (config: unknown) => Promise<MediaCapabilitiesProbeResult>;
      }
    ).encodingInfo;
    return encodingInfo
      ? (config) => encodingInfo.call(mediaCapabilities, config)
      : null;
  }

  const decodingInfo = mediaCapabilities.decodingInfo;
  return decodingInfo
    ? (config) =>
        decodingInfo.call(mediaCapabilities, config as MediaDecodingConfiguration)
    : null;
}

async function familyIsHardwareCapable(
  probe: MediaCapabilitiesProbe,
  representativeMimeType: string,
  video: HardwareProbeVideoTarget,
): Promise<boolean> {
  try {
    const info = await probe({
      type: 'webrtc',
      video: {
        contentType: toContentType(representativeMimeType),
        width: video.width,
        height: video.height,
        bitrate: video.bitrate,
        framerate: video.framerate,
      },
      // Non-standard Chromium hint, deliberately kept: it is what makes Chromium
      // answer for the hardware path, which is the only thing this probe exists to
      // discover. Chromium ignores unknown fields, so it is inert elsewhere.
      hardwareAcceleration: 'prefer-hardware',
    });
    return !!info.supported && !!info.powerEfficient;
  } catch {
    return false;
  }
}

/**
 * Orders codec *families* for hardware preference.
 *
 * @param mode `encode` uses encodingInfo (host); `decode` uses decodingInfo (client).
 * @param video Real session target; defaults to DEFAULT_HW_PROBE_VIDEO.
 */
export async function preferHardwareVideoCodecs<T extends RtpCodecCapabilityLike>(
  codecs: T[],
  options?: { mode?: HardwareCodecPreferenceMode; video?: HardwareProbeVideoTarget },
): Promise<T[]> {
  if (!codecs.length) return codecs;

  const mode: HardwareCodecPreferenceMode = options?.mode ?? 'decode';
  const video = options?.video ?? DEFAULT_HW_PROBE_VIDEO;
  const probe = resolveProbe(mode);
  if (!probe) return codecs;

  try {
    const familyOrder: string[] = [];
    const byFamily = new Map<string, T[]>();
    for (const codec of codecs) {
      const family = videoCodecFamily(codec.mimeType);
      let members = byFamily.get(family);
      if (!members) {
        members = [];
        byFamily.set(family, members);
        familyOrder.push(family);
      }
      members.push(codec);
    }

    const hwCapable = new Map<string, boolean>();
    await Promise.all(
      familyOrder.map(async (family) => {
        const representative = byFamily.get(family)![0];
        hwCapable.set(
          family,
          await familyIsHardwareCapable(probe, representative.mimeType, video),
        );
      }),
    );

    const hwRank = new Map<string, number>(
      HW_VIDEO_CODEC_FAMILY_ORDER.map((family, index) => [family, index]),
    );

    const hwFamilies = familyOrder
      .filter((family) => hwCapable.get(family) && hwRank.has(family))
      .sort((a, b) => (hwRank.get(a) ?? 99) - (hwRank.get(b) ?? 99));

    const softFamilies = familyOrder.filter((family) => !hwFamilies.includes(family));

    return [...hwFamilies, ...softFamilies].flatMap((family) => byFamily.get(family)!);
  } catch {
    return codecs;
  }
}
